import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  IconButton,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import {
  Upload,
  Download,
  Delete,
  CheckCircle,
  Error,
  Archive,
  ExpandMore,
  DeleteSweep,
} from '@mui/icons-material'
import {
  useCertificateContext,
  usePinContext,
  useStoredDocumentContext,
} from '../../providers'
import JSZip from 'jszip'
import { trpc } from '../../utils'

// Durum chip komponenti
const StatusChip: React.FC<{ status: string; error?: string }> = ({
  status,
  error,
}) => {
  switch (status) {
    case 'signed':
      return (
        <Chip
          icon={<CheckCircle />}
          label="İmzalandı"
          color="success"
          size="small"
        />
      )
    case 'failed':
      return (
        <Tooltip title={error || 'Bilinmeyen hata'}>
          <Chip icon={<Error />} label="Hata" color="error" size="small" />
        </Tooltip>
      )
    default:
      return <Chip label={status} size="small" />
  }
}

// Dosya boyutu formatlama
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Evrak listesi item komponenti
const DocumentListItem: React.FC<{
  document: any
  onDownload?: (id: string) => void
  onDelete: (id: string) => void
  showDownload?: boolean
}> = ({ document, onDownload, onDelete, showDownload = true }) => {
  return (
    <Accordion sx={{ border: 1, borderColor: 'divider', mb: 1 }}>
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          },
          py: { xs: 1.5, sm: 1 },
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0, // Prevents overflow
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Typography
            variant="body1"
            fontWeight="medium"
            sx={{
              mr: { xs: 0, sm: 2 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {document.originalName}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={{ xs: 0.5, sm: 1 }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            ml: { xs: 0, sm: 2 },
            alignItems: 'center',
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <StatusChip
            status={document.metadata.status}
            error={document.metadata.error}
          />
          {showDownload && onDownload && (
            <Tooltip title="İndir">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onDownload(document.id)}
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  minWidth: { xs: 32, sm: 40 },
                  minHeight: { xs: 32, sm: 40 },
                }}
              >
                <Download sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Sil">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(document.id)}
              sx={{
                p: { xs: 0.5, sm: 1 },
                minWidth: { xs: 32, sm: 40 },
                minHeight: { xs: 32, sm: 40 },
              }}
            >
              <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Boyut: {formatFileSize(document.metadata.size)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {document.metadata.status === 'failed' ? 'Deneme' : 'İmzalanma'}:{' '}
            {new Date(document.metadata.signedAt).toLocaleDateString('tr-TR')}{' '}
            {new Date(document.metadata.signedAt).toLocaleTimeString('tr-TR')}
          </Typography>
          {document.metadata.error && (
            <Typography
              variant="body2"
              color="error.main"
              sx={{
                mt: 1,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                wordBreak: 'break-word',
              }}
            >
              Hata: {document.metadata.error}
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

// Evrak listesi komponenti
const DocumentList: React.FC<{
  documents: any[]
  title: string
  color?: 'success' | 'error'
  onDownload?: (id: string) => void
  onDelete: (id: string) => void
}> = ({ documents, title, color = 'success', onDownload, onDelete }) => {
  if (documents.length === 0) return null

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6" color={`${color}.main`}>
          {title} ({documents.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {documents.map((document) => (
            <DocumentListItem
              key={document.id}
              document={document}
              onDownload={onDownload}
              onDelete={onDelete}
              showDownload={color === 'success'}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

// İlerleme çubuğu komponenti
const SigningProgress: React.FC<{
  signingProgress: { completed: number; failed: number; total: number }
}> = ({ signingProgress }) => {
  const progressPercentage =
    signingProgress.total > 0
      ? ((signingProgress.completed + signingProgress.failed) /
          signingProgress.total) *
        100
      : 0

  return (
    <Box sx={{ mb: 2 }}>
      <LinearProgress
        variant="determinate"
        value={progressPercentage}
        sx={{ mb: 1 }}
      />
      <Typography variant="body2" color="text.secondary" align="center">
        {signingProgress.completed + signingProgress.failed} /{' '}
        {signingProgress.total} dosya işlendi
        {signingProgress.completed > 0 &&
          ` (${signingProgress.completed} başarılı)`}
        {signingProgress.failed > 0 && ` (${signingProgress.failed} hata)`}
      </Typography>
    </Box>
  )
}

export const StoredDocumentSigning: React.FC = () => {
  const { selectedCertificate } = useCertificateContext()
  const { pinCode } = usePinContext()
  const {
    documents,
    loading,
    signAndStoreDocuments,
    downloadDocument,
    deleteDocument,
    deleteAllDocuments,
    signingLoading,
    signingProgress,
    refreshDocuments,
  } = useStoredDocumentContext()

  useEffect(() => {
    refreshDocuments()
  }, [])

  const [downloadingAll, setDownloadingAll] = useState(false)
  const [deletingAll, setDeletingAll] = useState(false)

  const signedDocuments = useMemo(
    () => documents.filter((doc) => doc.metadata.status === 'signed'),
    [documents],
  )

  const failedDocuments = useMemo(
    () => documents.filter((doc) => doc.metadata.status === 'failed'),
    [documents],
  )

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files
    if (!files || files.length === 0 || !selectedCertificate || !pinCode.trim())
      return

    const udfFiles = Array.from(files).filter((file) =>
      file.name.endsWith('.udf'),
    )
    if (udfFiles.length === 0) return

    await signAndStoreDocuments(udfFiles, {
      library: selectedCertificate.terminal.library,
      slotId: selectedCertificate.terminal.slotId,
      certSerialNumber: selectedCertificate.cert.serialNumber,
      pincode: pinCode,
      certificateBase64: selectedCertificate.cert.certificate,
      clientId: selectedCertificate.client.clientId,
      signingTime: new Date().toISOString(),
      signerName: selectedCertificate.cert.subjectCommonName,
    })

    event.target.value = ''
  }

  const handleDownloadAll = async () => {
    if (signedDocuments.length === 0) return

    setDownloadingAll(true)
    try {
      const zip = new JSZip()

      for (const document of signedDocuments) {
        try {
          const result = await trpc.downloadDocument.mutate({
            documentId: document.id,
          })

          if (result.success) {
            const fileBuffer = Buffer.from(result.fileBase64, 'base64')
            zip.file(document.signedFileName, fileBuffer)
          }
        } catch (error) {
          console.error(`Error downloading ${document.originalName}:`, error)
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(zipBlob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = `imzali_evraklar_${new Date().toISOString().split('T')[0]}.zip`
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error creating zip:', error)
    } finally {
      setDownloadingAll(false)
    }
  }

  const handleDeleteAll = async () => {
    if (documents.length === 0) return

    setDeletingAll(true)
    try {
      await deleteAllDocuments()
    } catch (error) {
      console.error('Error deleting all documents:', error)
    } finally {
      setDeletingAll(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Evrak İmzalama
        </Typography>

        {/* Sertifika uyarısı */}
        {!selectedCertificate && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Evrak imzalamak için önce bir sertifika seçin.
          </Alert>
        )}

        {/* Kontrol butonları */}
        {selectedCertificate && (
          //mobilde vertical olsun
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<Upload />}
              disabled={signingLoading}
            >
              {signingLoading
                ? 'İmzalanıyor...'
                : 'UDF Dosyalarını Seç ve İmzala'}
              <input
                type="file"
                accept=".udf"
                hidden
                onChange={handleFileUpload}
                multiple
              />
            </Button>

            {signedDocuments.length > 0 && (
              <Button
                variant="outlined"
                startIcon={
                  downloadingAll ? <CircularProgress size={20} /> : <Archive />
                }
                onClick={handleDownloadAll}
                disabled={downloadingAll}
              >
                TÜMÜNÜ İNDİR ({signedDocuments.length})
              </Button>
            )}

            {documents.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={
                  deletingAll ? <CircularProgress size={20} /> : <DeleteSweep />
                }
                onClick={handleDeleteAll}
                disabled={deletingAll}
              >
                TÜMÜNÜ SİL ({documents.length})
              </Button>
            )}
          </Box>
        )}

        {/* İlerleme çubuğu */}
        {signingLoading && (
          <SigningProgress signingProgress={signingProgress} />
        )}

        {/* İçerik */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Alert severity="info">Henüz imzalanmış evrak bulunmuyor</Alert>
        ) : (
          <Box>
            <DocumentList
              documents={signedDocuments}
              title="İmzalı Evraklar"
              color="success"
              onDownload={downloadDocument}
              onDelete={deleteDocument}
            />
            <DocumentList
              documents={failedDocuments}
              title="Hatalı Evraklar"
              color="error"
              onDelete={deleteDocument}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
